/**
 * Every date and time shown in the admin is the RESTAURANT's, never the browser's and never UTC.
 *
 * A manager in Tunis checking the evening from a hotel in Tokyo must read "19:30", the time
 * the ticket was taken in the room — not 03:30, and not 18:30 (UTC). So:
 *
 *  - the zone is the restaurant's (`restaurant_data.timezone`, IANA name; legacy "GMT+1" understood);
 *  - the language/format is the reader's (`activeLocale()` — the admin's language, not the OS);
 *  - "today", "this week", date filters are the restaurant's calendar days.
 *
 * Input contract with the API (see ods-be app/utils/restaurant_time.py): a datetime with an
 * offset is an exact instant; a naive "YYYY-MM-DDTHH:mm:ss" is the restaurant's wall-clock
 * time (pass `{ naive: 'utc' }` for the rare UTC column); a bare "YYYY-MM-DD" is a calendar
 * date and is never shifted.
 */
import { activeLocale } from '@/utils/locale';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const NAIVE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?$/;
const LEGACY_GMT = /^(?:GMT|UTC)\s*([+-])\s*(\d{1,2})(?::?(\d{2}))?$/i;

const validZones = new Map();
const isValidZone = (tz) => {
  if (!tz) return false;
  if (!validZones.has(tz)) {
    let ok = true;
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: tz });
    } catch {
      ok = false;
    }
    validZones.set(tz, ok);
  }
  return validZones.get(tz);
};

/** An IANA zone for a restaurant timezone name ("GMT+1" → "Etc/GMT-1"), or null. */
export const normalizeTimeZone = (name) => {
  if (!name || typeof name !== 'string') return null;
  const s = name.trim();
  const m = s.match(LEGACY_GMT);
  if (m) {
    const hours = parseInt(m[2], 10);
    if (m[3] && m[3] !== '00') return null; // no Etc/ zone for half hours
    const zone = hours === 0 ? 'UTC' : `Etc/GMT${m[1] === '+' ? '-' : '+'}${hours}`;
    return isValidZone(zone) ? zone : null;
  }
  return isValidZone(s) ? s : null;
};

const browserZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

let cachedRaw;
let cachedZone;
/**
 * The restaurant's time zone. Read from `restaurant_data` (stored at login / restaurant load);
 * the browser's zone only when the restaurant is not known yet.
 */
export const restaurantTz = () => {
  if (typeof window === 'undefined') return 'UTC';
  let raw = null;
  try {
    raw = window.localStorage.getItem('restaurant_data');
  } catch {
    raw = null;
  }
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    let tz = null;
    try {
      tz = normalizeTimeZone(JSON.parse(raw || 'null')?.timezone);
    } catch {
      tz = null;
    }
    cachedZone = tz;
  }
  return cachedZone || browserZone();
};

// ── parsing ────────────────────────────────────────────────────────────────

const partsFormatters = new Map();
const partsFormatter = (tz) => {
  if (!partsFormatters.has(tz)) {
    partsFormatters.set(tz, new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', weekday: 'short',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }));
  }
  return partsFormatters.get(tz);
};

const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** Wall-clock parts of an instant in a zone: { year, month (1-12), day, hour, minute, second, weekday (0=Sun) }. */
export const zonedParts = (date, tz = restaurantTz()) => {
  const out = {};
  for (const p of partsFormatter(tz).formatToParts(date)) {
    if (p.type === 'weekday') out.weekday = WEEKDAYS[p.value];
    else if (p.type !== 'literal') out[p.type] = parseInt(p.value, 10);
  }
  if (out.hour === 24) out.hour = 0;
  return out;
};

const offsetMs = (utcMs, tz) => {
  const p = zonedParts(new Date(utcMs), tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(utcMs / 1000) * 1000;
};

/** The instant at which the restaurant's clock reads the given wall time (DST-safe). */
export const wallToInstant = (y, mo, d, h = 0, mi = 0, s = 0, ms = 0, tz = restaurantTz()) => {
  const guess = Date.UTC(y, mo - 1, d, h, mi, s, ms);
  let t = guess - offsetMs(guess, tz);
  const second = guess - offsetMs(t, tz);
  if (second !== t) t = second; // crossed a DST change: use the offset in force at that time
  return new Date(t);
};

const toDate = (value, naive = 'wall') => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return Number.isFinite(value) ? new Date(value) : null;
  if (typeof value !== 'string') return null;
  const s = value.trim();
  let m = s.match(DATE_ONLY);
  if (m) return wallToInstant(+m[1], +m[2], +m[3], 12); // a calendar day: its midday, never shifted
  m = s.match(NAIVE);
  if (m) {
    const ms = m[7] ? parseInt(m[7].padEnd(3, '0').slice(0, 3), 10) : 0;
    if (naive === 'utc') return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0), ms));
    return wallToInstant(+m[1], +m[2], +m[3], +m[4], +m[5], +(m[6] || 0), ms);
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * An API value as an exact instant (Date) or null.
 * @param {string|number|Date} value
 * @param {{ naive?: 'wall'|'utc' }} [opts] how to read a value without offset (default: restaurant wall-clock)
 */
export const parseInstant = (value, opts = {}) => toDate(value, opts.naive || 'wall');

// ── formatting ─────────────────────────────────────────────────────────────

const formatters = new Map();
const formatter = (locale, options) => {
  const key = `${locale}|${JSON.stringify(options)}`;
  if (!formatters.has(key)) formatters.set(key, new Intl.DateTimeFormat(locale, options));
  return formatters.get(key);
};

const DATE_OPTS = { day: '2-digit', month: '2-digit', year: 'numeric' };
const TIME_OPTS = { hour: '2-digit', minute: '2-digit' };
const DATETIME_OPTS = { ...DATE_OPTS, ...TIME_OPTS };

const format = (value, options, opts = {}) => {
  const d = toDate(value, opts.naive || 'wall');
  if (!d) return opts.fallback ?? '';
  const timeZone = opts.timeZone || restaurantTz();
  return formatter(opts.locale || activeLocale(), { ...options, timeZone }).format(d);
};

/**
 * Format in the restaurant's zone and the reader's language. `options` are Intl options.
 * `opts`: { naive: 'wall'|'utc', locale, fallback }.
 */
export const formatDateTime = (value, options = DATETIME_OPTS, opts) => format(value, options, opts);
export const formatDate = (value, options = DATE_OPTS, opts) => format(value, options, opts);
export const formatTime = (value, options = TIME_OPTS, opts) => format(value, options, opts);

/** Intl formatToParts in the restaurant's zone (charts, custom layouts). */
export const formatToParts = (value, options = DATETIME_OPTS, opts = {}) => {
  const d = toDate(value, opts.naive || 'wall');
  if (!d) return [];
  return formatter(opts.locale || activeLocale(), { ...options, timeZone: restaurantTz() }).formatToParts(d);
};

// ── the restaurant's calendar ──────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, '0');

/** The restaurant-local wall-clock parts of a value (hour for buckets, weekday, …). */
export const restaurantParts = (value = new Date(), opts = {}) => {
  const d = toDate(value, opts.naive || 'wall');
  return d ? zonedParts(d) : null;
};

/** 'YYYY-MM-DD' of the restaurant's day for a value (default: now). */
export const toRestaurantYMD = (value = new Date(), opts = {}) => {
  if (typeof value === 'string' && DATE_ONLY.test(value.trim())) return value.trim();
  const p = restaurantParts(value, opts);
  return p ? `${p.year}-${pad(p.month)}-${pad(p.day)}` : '';
};

/** Today, in the restaurant ('YYYY-MM-DD'). */
export const restaurantToday = () => toRestaurantYMD(new Date());

/** Calendar arithmetic on 'YYYY-MM-DD' (no zone involved). */
export const addDaysYMD = (ymd, n) => {
  const m = String(ymd).match(DATE_ONLY);
  if (!m) return ymd;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3] + n));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};

/** Day of week (0 = Sunday) of a 'YYYY-MM-DD'. */
export const weekdayOfYMD = (ymd) => {
  const m = String(ymd).match(DATE_ONLY);
  return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay() : null;
};

/** 'YYYY-MM-DD' of a browser-local Date used as a pure calendar date (date pickers). */
export const ymdOfCalendarDate = (d) => (d instanceof Date && !Number.isNaN(d.getTime())
  ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : '');

/** A browser-local Date at midnight carrying the restaurant's Y-M-D (for calendar widgets). */
export const calendarDateOfYMD = (ymd) => {
  const m = String(ymd).match(DATE_ONLY);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};

/**
 * Format a pure CALENDAR day — a 'YYYY-MM-DD' or a browser-local Date built with new Date(y, m, d)
 * by a date picker / month grid — without any zone shift (month and weekday labels, day headers).
 * formatDate(new Date(y, m, d)) would read that local midnight as an instant and can land on the
 * previous day in the restaurant's zone.
 */
export const formatCalendarDate = (day, options = DATE_OPTS, opts) => {
  const ymd = day instanceof Date ? ymdOfCalendarDate(day) : day;
  return format(ymd, options, opts);
};

/** The restaurant's today as a calendar Date (widgets that work on local Date objects). */
export const restaurantTodayDate = () => calendarDateOfYMD(restaurantToday());

/** The exact instant of the restaurant's local midnight starting that day. */
export const restaurantDayStart = (ymd) => {
  const m = String(ymd).match(DATE_ONLY);
  return m ? wallToInstant(+m[1], +m[2], +m[3]) : null;
};

/**
 * The restaurant's naive wall-clock ISO ('YYYY-MM-DDTHH:mm:ss') of a value — what date filters
 * send to the API for wall-clock columns. A 'YYYY-MM-DD' gives its midnight.
 */
export const toRestaurantWallISO = (value = new Date(), opts = {}) => {
  if (typeof value === 'string' && DATE_ONLY.test(value.trim())) return `${value.trim()}T00:00:00`;
  const p = restaurantParts(value, opts);
  return p ? `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}` : '';
};

/** Whole minutes elapsed since a value (never negative: a clock slightly ahead reads 0). */
export const minutesSince = (value, now = Date.now(), opts = {}) => {
  const d = toDate(value, opts.naive || 'wall');
  if (!d) return null;
  return Math.max(0, Math.floor((now - d.getTime()) / 60000));
};

/** True when the value falls on the restaurant's today. */
export const isRestaurantToday = (value, opts) => toRestaurantYMD(value, opts) === restaurantToday();

/** Minutes since the restaurant's midnight (0-1439) — position on a day timeline. */
export const minutesOfDay = (value, opts) => {
  const p = restaurantParts(value, opts);
  return p ? p.hour * 60 + p.minute : null;
};

/** 'HH:mm' of the restaurant's clock for a value (inputs of type time). */
export const toRestaurantHM = (value, opts) => {
  const p = restaurantParts(value, opts);
  return p ? `${pad(p.hour)}:${pad(p.minute)}` : '';
};
